import {
	useContributionsPageData,
	useContributionsPageState,
} from '#src/app/pages/accountSettings/contributions/main/pageContext';
import { useCurrentUser } from '#src/app/shared/hooks/session';
import type { WithTokenParams } from '#src/app/shared/types/payment';
import type { PaymentType } from '#src/app/shared/utils/payments';

import { useFieldFocus } from './hooks/useFieldFocus';
import { useTokenError } from './hooks/useTokenError';
import { useValidate } from './hooks/useValidate';
import { useWithToken } from './hooks/useWithToken';

type Result = ModelHookResult<
	{
		loadingUpdatePaymentMethod: boolean;
		selectedPaymentType: PaymentType;
		email: string;
		userName: string;
		paymentMethodSaveOptions: {
			shouldSavePaymentMethod: boolean;
			usage: 'OFF_SESSION';
		};
		countryCode: string;
		currency: string;
	},
	{
		beforeToken: (paymentType: PaymentType) => Promise<boolean>;
		withToken: (params: WithTokenParams) => Promise<void>;
		onTokenError: (paymentType: PaymentType, err: Error) => void;
		onTokenInvalid: (paymentType: PaymentType, err: Error) => void;
		onCardDetailsFieldFocus: () => void;
		onFieldFocusIban: () => void;
		validate: () => Promise<boolean>;
	}
>;

export function useUpdatePaymentMethod(): Result {
	const currentUser = useCurrentUser();
	const [{ userName, loadingUpdatePaymentMethod, selectedPaymentType }, mutations] = useContributionsPageState();

	const {
		data: { countryCode, currency },
	} = useContributionsPageData();
	const {
		actions: { withToken },
	} = useWithToken();

	const {
		actions: { onTokenError, onTokenInvalid },
	} = useTokenError();
	const {
		actions: { onCardDetailsFieldFocus, onFieldFocusIban },
	} = useFieldFocus();
	const {
		actions: { validate },
	} = useValidate();

	const beforeToken = async () => {
		mutations.resetPageMessage();
		mutations.setLoadingUpdatePaymentMethod(true);
		return Promise.resolve(true);
	};

	return {
		data: {
			loadingUpdatePaymentMethod,
			selectedPaymentType,
			email: currentUser?.email || '',
			userName,
			paymentMethodSaveOptions: {
				shouldSavePaymentMethod: true,
				usage: 'OFF_SESSION',
			},
			countryCode,
			currency,
		},
		actions: {
			beforeToken,
			withToken,
			onTokenError,
			onTokenInvalid,
			onCardDetailsFieldFocus,
			onFieldFocusIban,
			validate,
		},
	};
}
